import { PenLine } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

import { useCenterStore } from '@caradasstore/CenterStore'
import { AdasFormControl, AdasSearchMenu } from '@components/wrapper-components'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { updateAssignee, updateTicketAssignee } from '../../api/api'
import { Logger } from '../../logger'
import './assignedUser.css'

// CAR-3639: which center users are eligible for an assignee/driver picker.
//   - includeAllUsers → everyone (e.g. the post-test-drive driver, who may be an admin/owner)
//   - isAdminUsersOnly → admins only
//   - default → non-admins only (regular assignee dropdowns)
// CAR-4080: client users (body shop staff) are never offered - /v1/users returns them with the
// centre's own staff, but work belongs to the calibration team.
export const filterAssigneeUsers = (userList = [], { isAdminUsersOnly = false, includeAllUsers = false } = {}) =>
  (userList || []).filter((user) => {
    if (user?.is_client_user) return false
    if (includeAllUsers) return true
    return isAdminUsersOnly ? user.is_admin : !user.is_admin
  })

// The filter can drop the record's current assignee (an admin on a regular picker, or a client
// user assigned before CAR-4080). Keep them in the list so the picker still shows who it is.
export const withCurrentAssignee = (users, currentAssignee) => {
  if (!currentAssignee?.id || users.some((user) => user.id === currentAssignee.id)) return users
  return [...users, currentAssignee]
}

export const AssignedUser = (props) => {
  const {
    initialAssignee,
    ticketNumber,
    workOrderId,
    closedStatus = false,
    isOpenStatus = false,
    type,
    isTicket = false,
    isAdminUsersOnly = false,
    includeAllUsers = false,
    onChange,
    updatePostTestDriveBy
  } = props

  const [assignee, setAssignee] = useState('')
  const [assigneeName, setAssigneeName] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  // Reassigning writes, so a client user sees the name without the pencil.
  const isReadOnly = useIsReadOnly()
  const containerRef = useRef(null)
  const userList = useCenterStore((store) => store.userList)

  const handleAssigneeChange = (e) => {
    if (updatePostTestDriveBy) {
      updatePostTestDriveBy(userList.find((user) => user.id === e.target.value))
      // Reflect the pick locally so the chip updates immediately instead of
      // waiting on the parent to round-trip the new initialAssignee back down.
      setAssignee(e.target.value)
      setIsEditing(false)
      return
    }
    if (!e.target.value && !isTicket) {
      return
    }
    const updateFunc = isTicket ? updateTicketAssignee : updateAssignee
    const id = isTicket ? ticketNumber : workOrderId

    const updateAssigneeResponse = updateFunc({
      id,
      userId: e.target.value || 0,
      type
    })

    updateAssigneeResponse
      .then((response) => {
        if (response.status === 200) {
          setAssignee(e.target.value)
          setIsEditing(false)
          if (onChange) {
            const assignedUser = userList.find((user) => user.id === e.target.value)
            if (assignedUser) onChange(assignedUser)
          }
        }
      })
      .catch((error) => {
        Logger.error({ message: error, payload: { file: 'assignedUser', method: 'updateAssignee' } })
      })
  }

  const generateOptions = () => {
    const currentAssignee = initialAssignee && assignee === initialAssignee.id ? initialAssignee : null
    const options = withCurrentAssignee(filterAssigneeUsers(userList, { isAdminUsersOnly, includeAllUsers }), currentAssignee).map(
      (user) => ({
        value: user.id,
        name: user.name
      })
    )

    options.sort((a, b) => a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()))
    if (!isOpenStatus) {
      options.push({ value: false, name: 'Unassigned' })
    }
    return options.filter(Boolean)
  }

  useEffect(() => {
    if (userList.length > 0) {
      const initialUserId =
        userList.find((user) => {
          return user?.id === initialAssignee?.id
        })?.id || false
      setAssignee(initialUserId)
    }
  }, [initialAssignee, userList])

  useEffect(() => {
    const assignedUser = userList.find((user) => user.id === assignee)
    // A client user's user list holds only itself (the backend never shows it the center's
    // staff), so the lookup misses; the record's own assignee still has the name.
    const readOnlyFallback = isReadOnly ? initialAssignee?.name : null
    setAssigneeName(assignedUser?.name || readOnlyFallback || 'Unassigned')
  }, [assignee, userList, isReadOnly, initialAssignee])

  useEffect(() => {
    if (!isEditing) return
    const handleClickOutside = (e) => {
      // The dropdown options render in a Radix portal (outside containerRef), so a
      // click on an option would otherwise look "outside" and close the editor on
      // mousedown — before the selection commits on click. Ignore popper clicks.
      if (e.target.closest?.('[data-radix-popper-content-wrapper]')) {
        return
      }
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsEditing(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isEditing])

  if (closedStatus) {
    return (
      <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-muted/50 text-[13px] text-muted-foreground'>
        {assigneeName}
      </span>
    )
  }

  // Read-only (client user): no editing, so label the name rather than showing it bare.
  if (isReadOnly) {
    return (
      <span className='inline-flex items-center gap-1.5 px-3 py-1 text-[13px] text-foreground'>
        {/* The post-test-drive picker carries its own "Performed by" label. */}
        {!updatePostTestDriveBy && <span className='text-muted-foreground'>Assignee:</span>}
        {assigneeName}
      </span>
    )
  }

  if (!isEditing) {
    return (
      <button
        onClick={() => setIsEditing(true)}
        className='inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-muted/50 border border-border text-[13px] text-foreground hover:bg-muted transition-colors'
      >
        {assigneeName}
        <PenLine className='w-3 h-3' />
      </button>
    )
  }

  return (
    <div ref={containerRef}>
      <AdasFormControl className='adas-form-control'>
        <AdasSearchMenu
          id='assignee'
          name='assignee'
          options={generateOptions()}
          value={assignee}
          onChange={handleAssigneeChange}
          className='max-w-[170px] h-8 rounded-lg border-border bg-muted/40 text-secondary text-[13px] px-3'
        />
      </AdasFormControl>
    </div>
  )
}

AssignedUser.propTypes = {
  initialAssignee: PropTypes.object,
  workOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ticketNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  closedStatus: PropTypes.bool,
  isTicket: PropTypes.bool,
  isAdminUsersOnly: PropTypes.bool,
  includeAllUsers: PropTypes.bool,
  onChange: PropTypes.func,
  updatePostTestDriveBy: PropTypes.func
}
